import { LeaseTerm } from '@common/entities';
import { queryClient } from 'AppWrapper';

import { getAvailableUnitsAndLeaseTerm } from 'services';
import { Unit } from 'types/floorplan-page-types';

const useGetAvailableUnitAndLeaseTerm = (locale = 'en-US') => {
  const getSelectedUnit = async (
    propertyId: number,
    floorPlanId: number,
    moveInDate: string,
    unitSpaceId: number,
    leaseId?: number,
  ) => {
    try {
      const queryKey = ['units', floorPlanId, moveInDate];
      if (leaseId) queryKey.push(leaseId);
      queryKey.push(locale);

      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          getAvailableUnitsAndLeaseTerm(propertyId, floorPlanId, moveInDate, leaseId, false),
      });

      const unit = data.find((i: Unit) => Number(i.unitSpaceId) === Number(unitSpaceId));
      if (!unit) throw new Error('Unit is not available.');

      return unit;
    } catch {
      throw new Error('Error while fetching available unit');
    }
  };

  const getSelectedLeaseTerm = (
    { leaseTerms }: Unit,
    termMonth?: string,
    leaseTermId?: number | string,
  ) => {
    if (leaseTermId != null) {
      const leaseTerm = leaseTerms.find(
        (item: LeaseTerm) => String(item.id) === String(leaseTermId),
      );
      if (leaseTerm) return leaseTerm;
    }

    if (!termMonth) return undefined;
    return leaseTerms.find((item: LeaseTerm) => String(item.termMonth) === String(termMonth));
  };

  async function fetchAvailableUnitsAndLeaseTerm(
    propertyId: number,
    floorPlanId: number,
    moveInDate: string,
    unitSpaceId: number,
    termMonth: string,
    leaseId?: number,
    leaseTermId?: number | string,
  ) {
    try {
      const unit = await getSelectedUnit(propertyId, floorPlanId, moveInDate, unitSpaceId, leaseId);
      if (!unit) throw new Error('Unit is not available.');

      if (!termMonth && leaseTermId == null) return { unit };

      const leaseTerm = getSelectedLeaseTerm(unit, termMonth, leaseTermId);
      return { unit, leaseTerm };
    } catch {
      throw new Error('Error while fetching available unit');
    }
  }

  return {
    fetchAvailableUnitsAndLeaseTerm,
    getSelectedUnit,
    getSelectedLeaseTerm,
  };
};

export default useGetAvailableUnitAndLeaseTerm;
